import { clsx } from "clsx";
import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Body } from "../Typography";
import "./Counter.css";

/**
 * Props shared by both Counter modes (numeric and plus/minus).
 */
interface CounterBaseProps {
  /**
   * The increment/decrement step amount
   * @default 1
   */
  step?: number;

  /**
   * When true, the value is constrained to multiples of step.
   * On blur, the value is rounded to the nearest valid step multiple.
   * @default false
   */
  strictStep?: boolean;

  /** Minimum value allowed */
  min?: number;

  /** Maximum value allowed */
  max?: number;

  /** Optional label displayed to the right of the counter */
  label?: string;

  /**
   * Suffix appended to the button labels (e.g. "°" renders "-5°" / "+5°").
   * Ignored when `allowPlusMinus` is set (the value carries its own modifier).
   * @default ""
   */
  suffix?: string;

  /**
   * Whether the counter is in an abnormal (highlighted) state.
   * Changes input and label to sky color scheme.
   * @default false
   */
  abnormal?: boolean;

  /**
   * Whether the counter is visually highlighted (sky-blue) — e.g. to flag a
   * value that differs from its default. Shares the sky treatment with
   * `abnormal`; kept as a separate prop so the "changed" and "abnormal finding"
   * concepts stay independent across the physical-exam inputs.
   * @default false
   */
  highlighted?: boolean;

  /** Whether the counter is disabled @default false */
  disabled?: boolean;

  /**
   * Width of the input in characters (used to set the input width)
   * @default 4
   */
  inputWidth?: number;

  /** Additional CSS class name for the root element */
  className?: string;

  /**
   * Accessible label for the value input, and the base for the increment /
   * decrement button labels (e.g. "Increase {aria-label}") so they stay unique
   * when several Counters share a page. Falls back to "Counter value" for the
   * input and "Increase/Decrease by {step}" for the buttons.
   */
  "aria-label"?: string;

  /**
   * Text shown in the (empty) input when the value is unset — i.e. numeric mode
   * with `value: null`, or plus/minus mode with an empty string. Lets a caller
   * render an "unanswered" placeholder (e.g. "–") distinct from a real 0.
   */
  placeholder?: string;
}

/**
 * Default numeric mode: value and onChange are numbers. `value` may be `null`
 * to render an unset/unanswered state (empty input, buttons enabled, stepping
 * off `min`); `onChange` only ever emits a number.
 */
export interface NumericCounterProps extends CounterBaseProps {
  allowPlusMinus?: false;
  allowLeadingPlus?: false;
  /** The current numeric value, or `null` when unset. */
  value: number | null;
  /** Fired with the new numeric value */
  onChange: (value: number) => void;
}

/**
 * Plus/minus mode: the +/- buttons still step the whole number, but the input
 * also accepts a trailing "+"/"-" so the value can be an ordinal like "2+"
 * (which sits between 2 and 3). `value` and `onChange` are strings here, and
 * `suffix` is ignored.
 */
export interface PlusMinusCounterProps extends CounterBaseProps {
  allowPlusMinus: true;
  allowLeadingPlus?: false;
  /** The current value, e.g. "2+", "3-", or "4" */
  value: string;
  /** Fired with the new string value, e.g. "3+" */
  onChange: (value: string) => void;
}

/**
 * Leading-plus mode: a numeric stepper whose value is a string carrying an
 * optional LEADING sign, so a value can be prefixed "+" to mark it (e.g.
 * hyperextension → "+30", rendered "+30°"). Unlike `allowPlusMinus` the sign is
 * a real numeric sign at the front (not a trailing ordinal), decimals are
 * allowed, and the `suffix` still renders. The "+" is opt-in per value: a plain
 * positive stays unsigned ("30"), so a normal degree never gains a spurious "+".
 */
export interface LeadingPlusCounterProps extends CounterBaseProps {
  allowLeadingPlus: true;
  allowPlusMinus?: false;
  /** The current value, e.g. "+30", "-10", "30", or "" when unset. */
  value: string;
  /** Fired with the new string value, e.g. "+35" */
  onChange: (value: string) => void;
}

export type CounterProps =
  | NumericCounterProps
  | PlusMinusCounterProps
  | LeadingPlusCounterProps;

type Sign = "" | "+" | "-";

const PLUS_MINUS_RE = /^(\d+)\s*([+-])?$/;

/** Parse a plus/minus scale string like "2+" into its integer + trailing sign. */
const parsePlusMinus = (raw: string): { num: number | null; sign: Sign } => {
  const match = raw.trim().match(PLUS_MINUS_RE);
  if (!match) return { num: null, sign: "" };
  const sign: Sign = match[2] === "+" || match[2] === "-" ? match[2] : "";
  return { num: Number.parseInt(match[1], 10), sign };
};

// Tolerate a trailing dot ("30." → 30) so tabbing away mid-decimal-entry
// commits rather than reverting, matching numeric mode's parseFloat.
const LEADING_SIGN_RE = /^([+-])?\s*(\d+(?:\.\d*)?)$/;

/** Parse a leading-sign string like "+30" into its signed number + sign. */
const parseLeadingSign = (raw: string): { num: number | null; sign: Sign } => {
  const match = raw.trim().match(LEADING_SIGN_RE);
  if (!match) return { num: null, sign: "" };
  const sign: Sign = match[1] === "+" || match[1] === "-" ? match[1] : "";
  return { num: Number.parseFloat(`${sign}${match[2]}`), sign };
};

/**
 * Keep an optional leading +/- and digits (one decimal) as the user types,
 * stripping the suffix. A lone sign is preserved so "+" can be typed before the
 * digits.
 */
const sanitizeLeadingSign = (raw: string, suffix: string): string => {
  const withoutSuffix = suffix ? raw.split(suffix).join("") : raw;
  const sign = withoutSuffix.trim().startsWith("-")
    ? "-"
    : withoutSuffix.trim().startsWith("+")
      ? "+"
      : "";
  const digits = withoutSuffix.replace(/[^0-9.]/g, "");
  return `${sign}${digits}`;
};

/** Null-safe string for the input; a null numeric value becomes "" (not "null"). */
const stringifyValue = (v: number | string | null): string =>
  v == null ? "" : String(v);

/** Keep digits plus a single trailing +/- as the user types. */
const sanitizePlusMinus = (raw: string): string => {
  const digits = raw.replace(/[^0-9]/g, "");
  if (!digits) return "";
  const signs = raw.match(/[+-]/g);
  const sign = signs ? signs[signs.length - 1] : "";
  return `${digits}${sign}`;
};

/**
 * Counter component for the Indigo design system.
 *
 * A numeric stepper with decrement/increment buttons flanking an editable input.
 * Supports step enforcement, min/max bounds, abnormal visual state, and an optional label.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Counter value={50} onChange={setValue} step={5} suffix="°" />
 *
 * // With label and abnormal state
 * <Counter value={30} onChange={setValue} step={5} suffix="°" label="Normal" abnormal />
 *
 * // Strict step enforcement
 * <Counter value={10} onChange={setValue} step={5} strictStep />
 *
 * // Plus/minus scale (value is a string like "2+")
 * <Counter allowPlusMinus value="2+" onChange={setScale} min={0} max={5} />
 *
 * // Leading-plus degree (value is a string like "+30" for hyperextension)
 * <Counter allowLeadingPlus value="+30" onChange={setDegree} suffix="°" />
 * ```
 */
export const Counter: FC<CounterProps> = (props) => {
  const {
    step = 1,
    strictStep = false,
    min,
    max,
    label,
    suffix = "",
    abnormal = false,
    highlighted = false,
    disabled = false,
    inputWidth = 4,
    className,
    placeholder,
    "aria-label": ariaLabelProp,
  } = props;

  const isPlusMinus = props.allowPlusMinus === true;
  const isLeadingPlus = props.allowLeadingPlus === true;
  // Both plus/minus and leading-plus carry their value as a string.
  const isStringMode = isPlusMinus || isLeadingPlus;
  const { value } = props;
  const onChange = props.onChange as (value: number | string) => void;

  const [inputValue, setInputValue] = useState(stringifyValue(value));
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync internal state when external value changes
  useEffect(() => {
    setInputValue(stringifyValue(value));
  }, [value]);

  // The current concrete number, or null when unset (numeric `null`, or a
  // plus/minus string with no digits). Buttons step off `min` from an unset
  // value and stay enabled, matching the pre-Counter Stepper behavior.
  const parsed = isPlusMinus
    ? parsePlusMinus(String(value))
    : isLeadingPlus
      ? parseLeadingSign(String(value))
      : null;
  const currentNumber: number | null = isStringMode
    ? (parsed?.num ?? null)
    : typeof value === "number"
      ? value
      : null;
  const currentSign: Sign = parsed?.sign ?? "";
  const baseNumber = currentNumber ?? min ?? 0;

  /** Clamp a number to [min, max] bounds */
  const clamp = useCallback(
    (val: number): number => {
      let clamped = val;
      if (min !== undefined) clamped = Math.max(min, clamped);
      if (max !== undefined) clamped = Math.min(max, clamped);
      return clamped;
    },
    [min, max],
  );

  /** Round value to nearest step multiple when strictStep is enabled */
  const snapToStep = useCallback(
    (val: number): number => {
      if (!strictStep) return val;
      const base = min ?? 0;
      return Math.round((val - base) / step) * step + base;
    },
    [strictStep, step, min],
  );

  /**
   * Commit a numeric value through clamp/snap and fire onChange. In plus/minus
   * mode the trailing sign is preserved and the emitted value is the string
   * `${number}${sign}`.
   */
  const commit = useCallback(
    (raw: number, signToUse: Sign) => {
      const clamped = clamp(snapToStep(raw));
      if (isPlusMinus) {
        // Trailing ordinal ("2" + "+" = "2+").
        const out = `${clamped}${signToUse}`;
        onChange(out);
        setInputValue(out);
      } else if (isLeadingPlus) {
        // Leading sign; only the "+" needs re-applying (String() already carries
        // "-" for negatives), and only while positive so 0/negatives stay plain.
        const out =
          signToUse === "+" && clamped > 0 ? `+${clamped}` : String(clamped);
        onChange(out);
        setInputValue(out);
      } else {
        onChange(clamped);
        setInputValue(String(clamped));
      }
    },
    [clamp, snapToStep, isPlusMinus, isLeadingPlus, onChange],
  );

  // Buttons step to a whole number and drop any trailing "+"/"-" ordinal; the
  // sign is only meaningful when typed directly into the input.
  // Leading-plus keeps the value's "+" across steps (so bumping "+30" gives
  // "+31", not "31"); plus/minus drops its trailing ordinal on a step.
  const stepSign: Sign = isLeadingPlus ? currentSign : "";

  const handleDecrement = useCallback(() => {
    commit(baseNumber - step, stepSign);
  }, [baseNumber, step, stepSign, commit]);

  const handleIncrement = useCallback(() => {
    commit(baseNumber + step, stepSign);
  }, [baseNumber, step, stepSign, commit]);

  const sanitizeNumeric = useCallback(
    (raw: string): string => {
      const withoutSuffix = suffix ? raw.split(suffix).join("") : raw;
      return withoutSuffix.replace(/,/g, ".").replace(/[^0-9.-]/g, "");
    },
    [suffix],
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(
        isPlusMinus
          ? sanitizePlusMinus(e.target.value)
          : isLeadingPlus
            ? sanitizeLeadingSign(e.target.value, suffix)
            : sanitizeNumeric(e.target.value),
      );
    },
    [isPlusMinus, isLeadingPlus, suffix, sanitizeNumeric],
  );

  const handleInputBlur = useCallback(() => {
    if (isPlusMinus) {
      const { num, sign: parsedSign } = parsePlusMinus(inputValue);
      if (num === null) {
        setInputValue(stringifyValue(value));
        return;
      }
      commit(num, parsedSign);
      return;
    }
    if (isLeadingPlus) {
      const { num, sign: parsedSign } = parseLeadingSign(inputValue);
      if (num === null) {
        setInputValue(stringifyValue(value));
        return;
      }
      commit(num, parsedSign);
      return;
    }
    const parsedNum = Number.parseFloat(inputValue);
    if (Number.isNaN(parsedNum)) {
      // Revert to current value on invalid/cleared input (keeps an unset value unset)
      setInputValue(stringifyValue(value));
      return;
    }
    commit(parsedNum, "");
  }, [isPlusMinus, isLeadingPlus, inputValue, value, commit]);

  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        (e.target as HTMLInputElement).blur();
      } else if (e.key === "Escape") {
        setInputValue(stringifyValue(value));
        (e.target as HTMLInputElement).blur();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        handleIncrement();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        handleDecrement();
      }
    },
    [value, handleIncrement, handleDecrement],
  );

  // Only append the suffix to a non-empty value, so an unset input stays blank
  // (and shows the placeholder) rather than rendering a lone "°"/"%".
  const displayValue =
    !isPlusMinus && suffix && inputValue !== ""
      ? `${inputValue}${suffix}`
      : inputValue;
  const dynamicInputWidth = Math.max(inputWidth, displayValue.length + 2);

  const buttonSuffix = isPlusMinus ? "" : suffix;
  // Disable a step only when there's a concrete value sitting at the bound; an
  // unset value leaves both buttons enabled so the first click seeds off min.
  const isDecrementDisabled =
    disabled ||
    (currentNumber != null && min !== undefined && currentNumber <= min);
  const isIncrementDisabled =
    disabled ||
    (currentNumber != null && max !== undefined && currentNumber >= max);

  // Fold the field's aria-label into the button labels so left/right/per-question
  // Counters on one page don't all share "Increase by {step}".
  const inputAriaLabel = ariaLabelProp ?? "Counter value";
  const decrementAriaLabel = ariaLabelProp
    ? `Decrease ${ariaLabelProp}`
    : `Decrease by ${step}`;
  const incrementAriaLabel = ariaLabelProp
    ? `Increase ${ariaLabelProp}`
    : `Increase by ${step}`;

  return (
    <div
      className={clsx(
        "indigo-counter",
        abnormal && "indigo-counter--abnormal",
        highlighted && "indigo-counter--highlighted",
        className,
      )}
    >
      {/* Decrement button */}
      <button
        type="button"
        className="indigo-counter__btn"
        onClick={handleDecrement}
        disabled={isDecrementDisabled}
        aria-label={decrementAriaLabel}
      >
        <Body as="span" size="xs">
          -{step}
          {buttonSuffix}
        </Body>
      </button>

      {/* Editable input */}
      <input
        ref={inputRef}
        type="text"
        inputMode={isStringMode ? "text" : "decimal"}
        className="indigo-counter__input"
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        style={{ width: `${dynamicInputWidth}ch` }}
        aria-label={inputAriaLabel}
      />

      {/* Increment button */}
      <button
        type="button"
        className="indigo-counter__btn"
        onClick={handleIncrement}
        disabled={isIncrementDisabled}
        aria-label={incrementAriaLabel}
      >
        <Body as="span" size="xs">
          +{step}
          {buttonSuffix}
        </Body>
      </button>

      {/* Optional label */}
      {label && (
        <Body as="span" size="sm" className="indigo-counter__label">
          {label}
        </Body>
      )}
    </div>
  );
};

Counter.displayName = "Counter";
